import {
  NativeSyntheticEvent,
  Platform,
  TextInputKeyPressEventData,
} from 'react-native';

/**
 * Universal key names for keyboard shortcuts
 */
export enum Key {
  Alt = 'Alt',
  ArrowDown = 'ArrowDown',
  ArrowUp = 'ArrowUp',
  Enter = 'Enter',
  Escape = 'Escape',
  Shift = 'Shift',
  Tab = 'Tab',
}

export const isMacOS = () => {
  return typeof window !== 'undefined'
    ? navigator.userAgent.includes('Mac') ||
        navigator.platform.toUpperCase().includes('MAC')
    : false;
};

/**
 * Get the modifier key label for a shortcut
 * Returns null for single-key shortcuts without modifiers
 */
export const getModifierKey = (hotKey: string | string[]): string | null => {
  const key = Array.isArray(hotKey) ? hotKey[0] : hotKey;

  if (key?.includes(Key.Shift)) {
    return 'Shift ⇧';
  }
  if (key?.includes(Key.Alt)) {
    return isMacOS() ? 'Option ⌥' : 'Alt ⌥';
  }

  // No modifier for single-key shortcuts
  return null;
};

/**
 * Check if a shortcut uses a modifier key (Alt, Shift, etc.)
 * Used to determine if shortcuts should be enabled on form inputs
 */
export const hasModifierKey = (hotKey: string | string[]): boolean => {
  const key = Array.isArray(hotKey) ? hotKey[0] : hotKey;
  return key?.includes(Key.Alt) || key?.includes(Key.Shift) || false;
};

/**
 * Get the display label for the main key of a shortcut
 * Handles special cases like arrows, slash, and enter
 */
export const getAppropriateShortcutKey = (
  hotKey: string | string[],
): string => {
  if (Array.isArray(hotKey)) {
    return '1-9';
  }
  if (hotKey.includes('Slash')) {
    return '/';
  }
  if (hotKey.includes(Key.Enter)) {
    return 'Enter ↵';
  }
  if (hotKey.includes(Key.ArrowUp)) {
    return '↑';
  }
  if (hotKey.includes(Key.ArrowDown)) {
    return '↓';
  }

  // Extract the main key (after modifier if present) and uppercase it
  const mainKey = hotKey.split('+').pop() ?? hotKey;
  return mainKey.toUpperCase();
};

/**
 * Returns whether the Shift modifier was pressed for a key event.
 * On web, uses the native KeyboardEvent.shiftKey; on other platforms returns false.
 *
 * @param e - React Native TextInput key press event
 */
export const isShiftPressed = (
  e: NativeSyntheticEvent<TextInputKeyPressEventData>,
): boolean => {
  if (Platform.OS !== 'web') {
    return false;
  }
  const nativeEvent = e.nativeEvent as KeyboardEvent;
  return nativeEvent.shiftKey === true;
};

/**
 * Returns whether a system modifier (Cmd/Ctrl) was pressed for a key event.
 * On web, uses the native KeyboardEvent; on other platforms returns false.
 * Used to allow browser-native shortcuts (Cmd+R, Cmd+C, etc.) to pass through.
 *
 * @param e - React Native TextInput key press event
 */
export const hasSystemModifierKey = (
  e: NativeSyntheticEvent<TextInputKeyPressEventData>,
): boolean => {
  if (Platform.OS !== 'web') {
    return false;
  }
  const nativeEvent = e.nativeEvent as KeyboardEvent;
  return nativeEvent.metaKey === true || nativeEvent.ctrlKey === true;
};

/**
 * Utility function to handle web key press events and prevent default behavior in inputs
 * Only forwards events with modifier keys (Alt, Shift) to allow global shortcuts while typing
 * Single-key events are NOT forwarded to avoid interfering with normal typing
 *
 * Note: if any inputs onKeyPress clashes with the global shortcuts
 * we need to add a check in the parent like so to prevent functions triggering ~
 *
 * if (typeof e.isDefaultPrevented === 'function' && e.isDefaultPrevented()) {
 *   return;
 * }
 *
 * @see QuantitySelector
 * @param e - the key press event
 */
export const handleWebKeyPress = (
  e: NativeSyntheticEvent<TextInputKeyPressEventData>,
) => {
  if (Platform.OS !== 'web') {
    return;
  }

  const nativeEvent = e.nativeEvent as KeyboardEvent;

  // Only forward events with modifier keys to avoid interfering with typing
  // Single-key shortcuts (c, e, /) should not fire while user is typing
  if (!nativeEvent.altKey && !nativeEvent.shiftKey && !nativeEvent.ctrlKey) {
    return;
  }

  // Forward modifier key events to the document to allow access to global shortcuts
  const keyboardEvent = new KeyboardEvent('keydown', e.nativeEvent);
  document.dispatchEvent(keyboardEvent);

  if (keyboardEvent.defaultPrevented) {
    e.preventDefault();
  }
};
